import { forwardRef, useMemo } from 'react'
import {
  Box,
  Button,
  Card,
  CardProps,
  Flex,
  Heading,
  Link,
  Message,
  Text,
  useAppTheme,
} from '@workwhile/ui'
import * as Popover from '@radix-ui/react-popover'
import { CircleDollarSign, Clock, ClockAlert, X } from 'lucide-react'
import { getLocationTimezone } from 'lib/time'
import { formatInTimeZone } from 'date-fns-tz'
import { formatCurrency } from 'lib/string'
import { getShiftBonus, getShiftPay } from 'lib/payment'
import { ShiftDetailBodySection } from '../ShiftDetailBodySection'
import { calculateEstimatedTimeWorked } from 'lib/work'
import { useShouldEnableLTARequestCallouts } from 'hooks/useShouldEnableLTARequestCallouts'
import { Work, WorkShift } from 'typings/common_defs'
import { useAssignmentWorkflowsContextStateValue } from '../..'

type WorkerShiftCellDetail = Omit<
  CardProps,
  'ref' | 'children' | 'shadowless'
> & {
  work?: Pick<
    Work,
    'status' | 'startedAt' | 'completedAt' | 'callouts' | 'earnings' | 'worker'
  >
  shift: Pick<
    WorkShift,
    | 'startsAt'
    | 'location'
    | 'endsAt'
    | 'id'
    | 'payLumpSum'
    | 'cancellationPolicy'
  >
}

export const WorkerShiftCellDetail = forwardRef<
  HTMLDivElement,
  WorkerShiftCellDetail
>(({ work, shift, ...rest }, ref) => {
  const { colors, space } = useAppTheme()
  const { removeWorkerFlow } = useAssignmentWorkflowsContextStateValue()
  const timezone = getLocationTimezone(shift.location)
  const startTime =
    shift.startsAt && formatInTimeZone(shift.startsAt, timezone, 'h:mm a')

  const endTime =
    shift.endsAt && formatInTimeZone(shift.endsAt, timezone, 'h:mm a z')

  const day =
    shift.startsAt && formatInTimeZone(shift.startsAt, timezone, 'EEEE, MMMM d')

  const shiftStatus = useMemo(() => {
    if (!work) {
      return {
        value: 'Unscheduled',
      }
    }

    const timezone = getLocationTimezone(shift.location)

    if (work.status === 'scheduled') {
      return {
        value: `Scheduled`,
      }
    }

    if (work.status === 'started') {
      const workerClockedInAt = work.startedAt
        ? formatInTimeZone(work.startedAt, timezone, 'h:mm a z')
        : undefined

      return {
        value: workerClockedInAt
          ? `Clocked in at ${workerClockedInAt}`
          : 'Working...',
      }
    }

    if (
      work.status &&
      [
        'completed',
        'approved',
        'paid',
        'employer_approved',
        'admin_review',
        'employer_review',
      ].includes(work.status)
    ) {
      const startTime = work.startedAt
        ? formatInTimeZone(work.startedAt, timezone, 'h:mm a')
        : undefined

      const endTime = work.completedAt
        ? formatInTimeZone(work.completedAt, timezone, 'h:mm a z')
        : undefined

      const estimatedTimeWorked = calculateEstimatedTimeWorked(work)

      return {
        value:
          startTime && endTime
            ? `${startTime} - ${endTime} (${estimatedTimeWorked})`
            : 'Completed',
      }
    }

    if (work.status === 'removed') {
      return {
        value: 'Removed from this shift',
      }
    }

    if (work.status === 'bailed') {
      return {
        icon: ClockAlert,
        color: colors.error100,
        value: 'No show',
      }
    }
  }, [work])

  const shiftPay = useMemo(() => {
    if (!work) {
      return undefined
    }

    if (work.status === 'scheduled') {
      const pay = getShiftPay(shift)
      const bonus = getShiftBonus(shift)
      const unit = shift.payLumpSum ? '' : '/hour'

      const payString = `${formatCurrency(pay)} ${unit}`
      const bonusString =
        bonus && Number(bonus) > 0 ? ` • Bonus: ${formatCurrency(bonus)}` : ''

      return `Est. ${payString}${bonusString}`
    }

    if (
      work.status &&
      [
        'completed',
        'approved',
        'paid',
        'employer_approved',
        'admin_review',
        'employer_review',
      ].includes(work.status)
    ) {
      const totalWorkerPay = work.earnings ? Math.max(work.earnings, 0) : 0

      return `Est. ${formatCurrency(totalWorkerPay)}`
    }

    if (work.status === 'removed') {
      return undefined
    }
  }, [work])

  const shouldEnableAssignmentCallouts = useShouldEnableLTARequestCallouts()

  const shouldShowCalloutRequestWarning = useMemo(() => {
    if (!work) {
      return false
    }

    if (shouldEnableAssignmentCallouts && work.status === 'scheduled') {
      // callouts array is sorted in ascending order by callouts.created_at, so we need to check the status of the most recent callout.
      const callout = work.callouts?.at(-1)
      const calloutStatus = callout?.status

      if (calloutStatus === 'requested') {
        return true
      }
    }
    return false
  }, [work])

  return (
    <Card ref={ref} width={[200, 320]} {...rest}>
      <Popover.Close
        asChild
        style={{ position: 'absolute', top: space[1], right: space[1] }}
      >
        <Button
          variant="text"
          p={1}
          style={{
            height: 'auto',
            padding: space[1],
          }}
        >
          <X size={18} />
        </Button>
      </Popover.Close>
      <Box>
        <Heading level={4} fontWeight="400" my={0}>
          {day}
        </Heading>
        {startTime && endTime ? (
          <Text
            color="lightText"
            fontSize={1}
          >{`${startTime} - ${endTime}`}</Text>
        ) : null}
      </Box>
      {shiftStatus ? (
        <ShiftDetailBodySection
          Icon={shiftStatus.icon || Clock}
          mt={3}
          iconColor={shiftStatus.color}
        >
          <Text color={shiftStatus.color}>{shiftStatus.value}</Text>
        </ShiftDetailBodySection>
      ) : null}

      {shiftPay ? (
        <ShiftDetailBodySection Icon={CircleDollarSign} mt={3}>
          <Text>{shiftPay}</Text>
        </ShiftDetailBodySection>
      ) : null}

      {shouldShowCalloutRequestWarning ? (
        <Message variant="error" mt={3}>
          <Text>
            <Text fontWeight="bold" as="b">
              Attention:
            </Text>{' '}
            This worker has requested a callout for this shift that needs your
            approval. Please <Link to={`/review`}>review the request here</Link>
            .
          </Text>
        </Message>
      ) : null}

      {work?.status === 'employer_review' ? (
        <Message variant="error" mt={3}>
          <Text>
            <Text fontWeight="bold" as="b">
              Attention:
            </Text>{' '}
            Please review this worker's shift details
          </Text>
        </Message>
      ) : null}

      {work?.status === 'admin_review' ? (
        <Message variant="warning" mt={3}>
          <Text>
            {' '}
            <Text fontWeight="bold" as="b">
              Attention:
            </Text>{' '}
            This shift is under review by WorkWhile
          </Text>
        </Message>
      ) : null}
      <Flex mt={3} justifyContent="flex-end">
        <Button as={Link} to={`/shift/${shift.id}`} kind="medium">
          <Text color="white">View Shift</Text>
        </Button>
      </Flex>
      <Flex mt={1} justifyContent="flex-end">
        {work?.worker && work.status === 'scheduled' ? (
          <Button
            variant="text"
            kind="medium"
            onClick={() => removeWorkerFlow.open(work?.worker, shift)}
          >
            <Text color="red">Remove {work?.worker?.name}</Text>
          </Button>
        ) : null}
      </Flex>
    </Card>
  )
})

WorkerShiftCellDetail.displayName = 'WorkerShiftCellDetail'
